import React, { useState } from 'react';
import {
  reportDate,
  goLiveDate,
  totalProperties,
  execSummary,
  properties,
  integrationStats,
  assetStats,
  siteWideIntegrations,
  pagesBuilt,
  customerActions,
  timeline,
  outstanding,
  clientFeedback,
} from '../progress-report-data';

// ─── Utility Components ──────────────────────────────────────────────

function Badge({ status }: { status: string }) {
  const styles: Record<string, string> = {
    ready: 'bg-emerald-100 text-emerald-800',
    'in-progress': 'bg-amber-100 text-amber-800',
    cancelled: 'bg-gray-100 text-gray-700',
    blocked: 'bg-red-100 text-red-800',
    'needs-attention': 'bg-orange-100 text-orange-800',
    'not-started': 'bg-gray-100 text-gray-600',
    pending: 'bg-blue-100 text-blue-700',
    done: 'bg-emerald-100 text-emerald-800',
    high: 'bg-red-50 text-red-700 ring-1 ring-red-200',
    medium: 'bg-amber-50 text-amber-700 ring-1 ring-amber-200',
    low: 'bg-gray-50 text-gray-600 ring-1 ring-gray-200',
  };
  const labels: Record<string, string> = {
    ready: 'Ready',
    'in-progress': 'In Progress',
    cancelled: 'Cancelled',
    blocked: 'Blocked',
    'needs-attention': 'Needs Attention',
    'not-started': 'Not Started',
    pending: 'Pending',
    done: 'Done',
    high: 'High',
    medium: 'Medium',
    low: 'Low',
  };
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${styles[status] || 'bg-gray-100 text-gray-700'}`}>
      {labels[status] || status}
    </span>
  );
}

function getTaskStatus(task: { done?: boolean; status?: string }) {
  if (task.done) return 'done';
  return task.status || 'not-started';
}

function isTaskClosed(task: { done?: boolean; status?: string }) {
  const status = getTaskStatus(task);
  return status === 'done' || status === 'cancelled';
}

function ProgressBar({ count, total: t }: { count: number; total: number }) {
  const pct = Math.round((count / t) * 100);
  return (
    <div className="flex items-center gap-3">
      <div className="flex-1 bg-gray-200 rounded-full h-2.5 overflow-hidden">
        <div
          className={`h-full rounded-full transition-all ${pct === 100 ? 'bg-emerald-500' : pct >= 80 ? 'bg-blue-500' : 'bg-amber-500'}`}
          style={{ width: `${pct}%` }}
        />
      </div>
      <span className="text-sm text-gray-600 tabular-nums w-16 text-right">{count}/{t}</span>
    </div>
  );
}

function StatusDot({ ok }: { ok: boolean }) {
  return ok
    ? <span className="inline-block w-3 h-3 rounded-full bg-emerald-500" title="Configured" />
    : <span className="inline-block w-3 h-3 rounded-full bg-gray-300" title="Not applicable" />;
}

function Collapsible({ title, count, children }: { title: string; count: number; children: React.ReactNode }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-gray-200 rounded-lg">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between p-4 text-left hover:bg-gray-50 transition-colors"
      >
        <span className="font-medium text-gray-900">{title}</span>
        <span className="flex items-center gap-2">
          <span className="text-sm text-gray-500">{count} properties</span>
          <svg className={`w-4 h-4 text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </span>
      </button>
      {open && <div className="px-4 pb-4 border-t border-gray-100">{children}</div>}
    </div>
  );
}

const reportSections = [
  { id: 'executive-summary', label: 'Executive Summary' },
  { id: 'timeline-to-go-live', label: 'Timeline to Go-Live' },
  { id: 'outstanding-items', label: 'Outstanding Items' },
  { id: 'client-feedback-staging-audit', label: 'Client Feedback - Staging Audit' },
  { id: 'site-wide-integrations', label: 'Site-Wide Integrations' },
  { id: 'per-property-data-coverage', label: 'Per-Property Data Coverage' },
  { id: 'pages-built', label: 'Pages Built' },
  { id: 'property-coverage-matrix', label: 'Property Coverage Matrix' },
] as const;

function getSectionUrl(sectionId: string) {
  if (typeof window === 'undefined') {
    return `#${sectionId}`;
  }

  const url = new URL(window.location.href);
  url.hash = sectionId;
  return url.toString();
}

async function copySectionUrl(sectionId: string) {
  const url = getSectionUrl(sectionId);

  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(url);
    return;
  }

  const input = document.createElement('input');
  input.value = url;
  input.setAttribute('readonly', '');
  input.style.position = 'absolute';
  input.style.left = '-9999px';
  document.body.appendChild(input);
  input.select();
  document.execCommand('copy');
  document.body.removeChild(input);
}

function SectionTitle({ id, children }: { id: string; children: React.ReactNode }) {
  const [copied, setCopied] = useState(false);
  const title = typeof children === 'string' ? children : 'section';

  const handleCopy = async () => {
    try {
      await copySectionUrl(id);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1800);
    } catch (error) {
      console.error(`Failed to copy section link for ${id}`, error);
    }
  };

  return (
    <div id={id} className="group scroll-mt-24 mt-16 first:mt-0 mb-6 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
      <h2 className="text-2xl font-bold text-gray-900">{children}</h2>
      <div className="flex flex-wrap items-center gap-2 opacity-100 transition-opacity md:opacity-0 md:group-hover:opacity-100 md:group-focus-within:opacity-100">
        <a
          href={`#${id}`}
          className="inline-flex items-center gap-2 rounded-full border border-gray-200 bg-white px-3 py-1.5 text-xs font-medium text-gray-600 transition-colors hover:border-gray-300 hover:text-gray-900"
          aria-label={`Jump to ${title}`}
          title={`Jump to ${title}`}
        >
          <svg className="h-3.5 w-3.5" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" d="M8.75 11.25 11.25 8.75M6.25 13.75l-1.5 1.5a2.5 2.5 0 1 1-3.5-3.5l3-3a2.5 2.5 0 0 1 3.5 0M13.75 6.25l1.5-1.5a2.5 2.5 0 1 1 3.5 3.5l-3 3a2.5 2.5 0 0 1-3.5 0" />
          </svg>
          Link
        </a>
        <button
          type="button"
          onClick={handleCopy}
          className="inline-flex items-center gap-2 rounded-full border border-gray-200 bg-white px-3 py-1.5 text-xs font-medium text-gray-600 transition-colors hover:border-gray-300 hover:text-gray-900"
          aria-label={`Copy direct link to ${title}`}
          title={`Copy direct link to ${title}`}
        >
          <svg className="h-3.5 w-3.5" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
            <rect x="6.5" y="6.5" width="9" height="9" rx="1.75" />
            <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.5h-1a1 1 0 0 1-1-1v-8a1 1 0 0 1 1-1h8a1 1 0 0 1 1 1v1" />
          </svg>
          {copied ? 'Copied' : 'Copy link'}
        </button>
      </div>
    </div>
  );
}

function SectionJumpNav() {
  return (
    <Card className="mb-12 p-5">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.24em] text-gray-400">Quick links</p>
          <p className="mt-2 max-w-2xl text-sm text-gray-600">
            Jump to any part of the report, then use the heading controls to copy a direct share link for that section.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {reportSections.map(section => (
            <a
              key={section.id}
              href={`#${section.id}`}
              className="inline-flex items-center rounded-full border border-gray-200 bg-gray-50 px-3 py-1.5 text-xs font-medium text-gray-700 transition-colors hover:border-gray-300 hover:bg-white hover:text-gray-900"
            >
              {section.label}
            </a>
          ))}
        </div>
      </div>
    </Card>
  );
}

function Card({ children, className = '' }: { children: React.ReactNode; className?: string }) {
  return <div className={`bg-white rounded-xl border border-gray-200 shadow-sm ${className}`}>{children}</div>;
}

// ─── Sections ────────────────────────────────────────────────────────

function HeroBanner() {
  return (
    <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 text-white p-8 md:p-12 mb-12">
      <div className="absolute inset-0 opacity-10">
        {/* Subtle geometric pattern */}
        <svg className="w-full h-full" viewBox="0 0 400 200" fill="none" xmlns="http://www.w3.org/2000/svg">
          {[...Array(8)].map((_, i) => (
            <rect key={i} x={i * 55 - 10} y={i * 15 + 20} width="60" height="60" rx="4" stroke="white" strokeWidth="1" transform={`rotate(${i * 5} ${i * 55 + 20} ${i * 15 + 50})`} />
          ))}
        </svg>
      </div>
      <div className="relative z-10">
        <p className="text-sm font-medium text-gray-400 uppercase tracking-wider mb-2">Case & Associates</p>
        <h1 className="text-3xl md:text-4xl font-bold mb-3">Implementation Progress Report</h1>
        <div className="flex flex-wrap gap-6 text-sm text-gray-300 mt-4">
          <span>Report Date: <strong className="text-white">{reportDate}</strong></span>
          <span>Target Go-Live: <strong className="text-emerald-400">{goLiveDate}</strong></span>
          <span>Properties: <strong className="text-white">{totalProperties}</strong></span>
        </div>
      </div>
    </div>
  );
}

function ExecSummary() {
  return (
    <>
      <SectionTitle id="executive-summary">Executive Summary</SectionTitle>
      {(execSummary as any).actionRequired && (
        <div className="mb-6 p-4 rounded-lg bg-blue-50 border border-blue-200 flex items-start gap-3">
          <svg className="w-5 h-5 text-blue-500 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
          <p className="text-sm text-blue-800"><strong>Action Required:</strong> {(execSummary as any).actionRequired}</p>
        </div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
        <Card className="p-6">
          <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
            <span className="inline-block w-2 h-2 rounded-full bg-emerald-500" />
            Highlights
          </h3>
          <ul className="space-y-2">
            {execSummary.highlights.map((h, i) => (
              <li key={i} className="text-sm text-gray-700 flex items-start gap-2">
                <svg className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                {h}
              </li>
            ))}
          </ul>
        </Card>
        <div className="space-y-6">
          {execSummary.blockers.length > 0 && (
            <Card className="p-6 ring-1 ring-red-200">
              <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                <span className="inline-block w-2 h-2 rounded-full bg-red-500" />
                Blockers
              </h3>
              <ul className="space-y-2">
                {execSummary.blockers.map((b, i) => (
                  <li key={i} className="text-sm text-gray-700">{b}</li>
                ))}
              </ul>
            </Card>
          )}
          {execSummary.propertyGaps.length > 0 && (
            <Card className="p-6 ring-1 ring-orange-200">
              <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                <span className="inline-block w-2 h-2 rounded-full bg-orange-500" />
                Property Discrepancies
              </h3>
              <ul className="space-y-2">
                {execSummary.propertyGaps.map((g, i) => (
                  <li key={i} className="text-sm text-gray-700">
                    <strong>{g.name}</strong> — {g.issue}
                  </li>
                ))}
              </ul>
            </Card>
          )}
        </div>
      </div>
    </>
  );
}

function TimelineSection() {
  const isCurrent = (label: string) => label === 'Current';
  const isGoLive = (label: string) => label === 'Go-Live';

  return (
    <>
      <SectionTitle id="timeline-to-go-live">Timeline to Go-Live</SectionTitle>
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
        {timeline.map((t, i) => {
          let ringClass = '';
          if (isCurrent(t.label)) ringClass = 'ring-2 ring-orange-400';
          else if (isGoLive(t.label)) ringClass = 'ring-2 ring-emerald-500';

          return (
            <Card key={i} className={`p-5 relative overflow-hidden ${ringClass}`}>
              {isGoLive(t.label) && (
                <span className="absolute top-2 right-2 text-2xl" role="img" aria-label="finish">&#127937;</span>
              )}
              <p className={`text-xs font-semibold uppercase tracking-wider mb-1 ${isCurrent(t.label) ? 'text-orange-500' : 'text-gray-400'}`}>{t.label}</p>
              <p className="text-sm font-bold text-gray-900 mb-2">{t.week}</p>
              <p className="text-sm text-gray-600">{t.desc}</p>
            </Card>
          );
        })}
      </div>
    </>
  );
}

function PagesSection() {
  return (
    <>
      <SectionTitle id="pages-built">Pages Built</SectionTitle>
      <Card className="overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-gray-50 border-b border-gray-200">
              <th className="text-left p-4 font-semibold text-gray-700">Page</th>
              <th className="text-left p-4 font-semibold text-gray-700">URL Pattern</th>
              <th className="text-left p-4 font-semibold text-gray-700">Notes</th>
            </tr>
          </thead>
          <tbody>
            {pagesBuilt.map((page, i) => (
              <tr key={i} className="border-b border-gray-100 last:border-0">
                <td className="p-4 font-medium text-gray-900">{page.name}</td>
                <td className="p-4 text-gray-600 font-mono text-xs">{page.path}</td>
                <td className="p-4 text-gray-500">{(page as any).note || ''}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </>
  );
}

function OutstandingItem({ item }: { item: typeof outstanding[number] }) {
  const [open, setOpen] = useState(false);
  const tasks = (item as any).tasks || [];
  const doneCount = tasks.filter((t: any) => isTaskClosed(t)).length;
  const totalCount = tasks.length;
  const allDone = totalCount > 0 && doneCount === totalCount;
  const hasWarning = (item as any).warning;
  const pct = totalCount > 0 ? Math.round((doneCount / totalCount) * 100) : 0;

  return (
    <Card className={`overflow-hidden ${hasWarning ? 'ring-1 ring-amber-300' : ''}`}>
      <button
        onClick={() => setOpen(!open)}
        className="w-full p-5 text-left hover:bg-gray-50 transition-colors"
      >
        <div className="flex items-start justify-between gap-4 mb-1">
          <h3 className="font-semibold text-gray-900 flex items-center gap-2">
            {hasWarning && <span title="Installed with known limitations">&#9888;&#65039;</span>}
            {item.title}
          </h3>
          <div className="flex items-center gap-2 flex-shrink-0">
            <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${allDone ? 'bg-emerald-100 text-emerald-800' : 'bg-gray-100 text-gray-600'}`}>
              {doneCount} of {totalCount} tasks
            </span>
            <svg className={`w-4 h-4 text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </div>
        </div>
        <p className="text-sm text-gray-500 mb-2">{item.desc}</p>
        <div className="bg-gray-200 rounded-full h-1.5 overflow-hidden">
          <div
            className={`h-full rounded-full transition-all ${allDone ? 'bg-emerald-500' : pct >= 50 ? 'bg-blue-500' : 'bg-amber-500'}`}
            style={{ width: `${pct}%` }}
          />
        </div>
      </button>
      {open && tasks.length > 0 && (
        <div className="px-5 pb-5 border-t border-gray-100 pt-3">
          <ul className="space-y-2">
            {tasks.map((t: any, j: number) => (
              <li key={j} className="flex items-start gap-2 text-sm">
                {getTaskStatus(t) === 'done' ? (
                  <svg className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                ) : getTaskStatus(t) === 'cancelled' ? (
                  <span className="w-4 h-4 mt-0.5 flex-shrink-0 rounded-full bg-gray-400 inline-block" />
                ) : getTaskStatus(t) === 'in-progress' ? (
                  <span className="w-4 h-4 mt-0.5 flex-shrink-0 rounded-full bg-amber-400 inline-block" />
                ) : (
                  <span className="w-4 h-4 mt-0.5 flex-shrink-0 rounded border-2 border-gray-300 inline-block" />
                )}
                <div className="min-w-0 flex-1">
                  <div className="flex items-start justify-between gap-3">
                    <span className={getTaskStatus(t) === 'done' ? 'text-gray-500' : 'text-gray-900'}>
                      {t.label}
                      {t.note && !t.href && <span className="text-xs text-gray-400 ml-2">({t.note})</span>}
                    </span>
                    <Badge status={getTaskStatus(t)} />
                  </div>
                  {t.href && (
                    <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs">
                      {t.note && <span className="text-gray-500">{t.note}</span>}
                      <a
                        href={t.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="font-medium text-blue-600 hover:text-blue-700 hover:underline"
                      >
                        {t.linkText || 'Open link'}
                      </a>
                    </div>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </Card>
  );
}

function OutstandingSection() {
  return (
    <>
      <SectionTitle id="outstanding-items">Outstanding Items</SectionTitle>
      <div className="space-y-3">
        {outstanding.map((item, i) => (
          <OutstandingItem key={i} item={item} />
        ))}
      </div>
    </>
  );
}

function ClientFeedbackSection() {
  const totalTasks = clientFeedback.reduce((sum, item) => sum + ((item as any).tasks || []).length, 0);
  const doneTasks = clientFeedback.reduce((sum, item) => sum + ((item as any).tasks || []).filter((t: any) => isTaskClosed(t)).length, 0);
  const templateItems = clientFeedback.filter(item => !item.title.includes('Content —'));
  const contentItems = clientFeedback.filter(item => item.title.includes('Content —'));

  return (
    <>
      <SectionTitle id="client-feedback-staging-audit">Client Feedback — Staging Audit</SectionTitle>
      <Card className="mb-4">
        <div className="p-5">
          <div className="flex items-center justify-between mb-3">
            <p className="text-sm text-gray-500">Feedback received from Case & Associates staging review. Template fixes are in progress; content items are queued for editor updates.</p>
            <span className="text-sm font-medium text-gray-600 flex-shrink-0 ml-4">{doneTasks}/{totalTasks} tasks</span>
          </div>
          <ProgressBar count={doneTasks} total={totalTasks} />
        </div>
      </Card>
      {templateItems.length > 0 && (
        <>
          <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-2 mt-6">Template / Theme Fixes</h3>
          <div className="space-y-3">
            {templateItems.map((item, i) => (
              <OutstandingItem key={`cf-tmpl-${i}`} item={item as any} />
            ))}
          </div>
        </>
      )}
      {contentItems.length > 0 && (
        <>
          <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-2 mt-6">Content / Editor Changes</h3>
          <div className="space-y-3">
            {contentItems.map((item, i) => (
              <OutstandingItem key={`cf-content-${i}`} item={item as any} />
            ))}
          </div>
        </>
      )}
    </>
  );
}

function SiteWideSection() {
  return (
    <>
      <SectionTitle id="site-wide-integrations">Site-Wide Integrations</SectionTitle>
      <div className="mb-4 p-4 rounded-lg bg-amber-50 border border-amber-200 flex items-start gap-3">
        <span className="text-amber-500 text-lg mt-0.5">&#9888;&#65039;</span>
        <p className="text-sm text-amber-800"><strong>Needs customer feedback.</strong> Several integrations require coordination with the Case team before they can be finalized. Please review the status and notes below.</p>
      </div>
      <p className="text-sm text-gray-600 mb-4">These are configured once and apply to every property page automatically.</p>
      <Card className="overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-gray-50 border-b border-gray-200">
              <th className="text-left p-4 font-semibold text-gray-700">Integration</th>
              <th className="text-left p-4 font-semibold text-gray-700">Status</th>
              <th className="text-left p-4 font-semibold text-gray-700">Notes</th>
            </tr>
          </thead>
          <tbody>
            {siteWideIntegrations.map((int, i) => (
              <tr key={i} className="border-b border-gray-100 last:border-0">
                <td className="p-4 font-medium text-gray-900">{int.label}</td>
                <td className="p-4"><Badge status={int.status} /></td>
                <td className="p-4 text-gray-500">{(int as any).note || ''}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </>
  );
}

function PerPropertySection() {
  const allStats = [...integrationStats, ...assetStats];
  return (
    <>
      <SectionTitle id="per-property-data-coverage">Per-Property Data Coverage</SectionTitle>
      <p className="text-sm text-gray-600 mb-4">Each property may have different integrations depending on what services it uses.</p>
      <Card className="overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-gray-50 border-b border-gray-200">
              <th className="text-left p-4 font-semibold text-gray-700 w-48">Integration</th>
              <th className="text-left p-4 font-semibold text-gray-700">Coverage</th>
            </tr>
          </thead>
          <tbody>
            {allStats.map((stat, i) => (
              <tr key={i} className="border-b border-gray-100 last:border-0">
                <td className="p-4 font-medium text-gray-900">{stat.label}</td>
                <td className="p-4"><ProgressBar count={stat.count} total={stat.total} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      {/* Gap details */}
      <div className="mt-6 space-y-3">
        {allStats.filter(s => s.count < s.total && s.count > 0).map((stat, i) => {
          const missing = properties.filter(p => {
            if (stat.key in (properties[0]?.integrations || {})) return !p.integrations[stat.key as keyof typeof p.integrations];
            if (stat.key in (properties[0]?.assets || {})) return !p.assets[stat.key as keyof typeof p.assets];
            return false;
          });
          if (missing.length === 0) return null;
          return (
            <Collapsible key={i} title={`${stat.label} — missing on`} count={missing.length}>
              <ul className="mt-3 space-y-1">
                {missing.map((p, j) => (
                  <li key={j} className="text-sm">
                    {p.newPath
                      ? <a href={p.newPath} className="text-blue-600 hover:underline">{p.name}</a>
                      : <span className="text-gray-700">{p.name}</span>
                    }
                  </li>
                ))}
              </ul>
            </Collapsible>
          );
        })}
      </div>
    </>
  );
}

function CustomerActionsSection() {
  return (
    <>
      <SectionTitle id="what-we-need-from-you">What We Need From You</SectionTitle>
      <Card className="overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-gray-50 border-b border-gray-200">
              <th className="text-left p-4 font-semibold text-gray-700">Item</th>
              <th className="text-left p-4 font-semibold text-gray-700 w-32">Owner</th>
              <th className="text-left p-4 font-semibold text-gray-700 w-24">Priority</th>
              <th className="text-left p-4 font-semibold text-gray-700 w-28">Status</th>
            </tr>
          </thead>
          <tbody>
            {customerActions.map((action, i) => (
              <tr key={i} className="border-b border-gray-100 last:border-0">
                <td className="p-4 text-gray-900">{action.item}</td>
                <td className="p-4 text-gray-600">{action.owner}</td>
                <td className="p-4"><Badge status={action.priority} /></td>
                <td className="p-4"><Badge status={action.status} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </>
  );
}

function PropertyMatrix() {
  const [search, setSearch] = useState('');
  const filtered = search
    ? properties.filter(p => p.name.toLowerCase().includes(search.toLowerCase()))
    : properties;

  const intKeys = integrationStats.map(s => ({ key: s.key, label: s.label }));
  const assetKeys = assetStats.map(s => ({ key: s.key, label: s.label }));

  return (
    <>
      <SectionTitle id="property-coverage-matrix">Property Coverage Matrix</SectionTitle>
      <div className="mb-4">
        <input
          type="text"
          placeholder="Search properties..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="w-full md:w-80 px-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
        />
      </div>
      <Card className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="bg-gray-50 border-b border-gray-200">
              <th className="text-left p-3 font-semibold text-gray-700 sticky left-0 bg-gray-50 min-w-[200px]">Property</th>
              {intKeys.map(k => (
                <th key={k.key} className="p-3 font-semibold text-gray-700 text-center whitespace-nowrap">{k.label}</th>
              ))}
              {assetKeys.map(k => (
                <th key={k.key} className="p-3 font-semibold text-gray-700 text-center whitespace-nowrap">{k.label}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filtered.map((prop, i) => (
              <tr key={i} className="border-b border-gray-100 last:border-0 hover:bg-gray-50">
                <td className="p-3 font-medium text-gray-900 sticky left-0 bg-white">
                  {prop.newPath
                    ? <a href={prop.newPath} className="text-blue-600 hover:underline">{prop.name}</a>
                    : prop.name
                  }
                </td>
                {intKeys.map(k => (
                  <td key={k.key} className="p-3 text-center">
                    <StatusDot ok={prop.integrations[k.key as keyof typeof prop.integrations]} />
                  </td>
                ))}
                {assetKeys.map(k => (
                  <td key={k.key} className="p-3 text-center">
                    <StatusDot ok={prop.assets[k.key as keyof typeof prop.assets]} />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
      <p className="text-xs text-gray-400 mt-2">
        <span className="inline-block w-2.5 h-2.5 rounded-full bg-emerald-500 mr-1 align-middle" /> Configured
        {' '}<span className="inline-block w-2.5 h-2.5 rounded-full bg-gray-300 mr-1 ml-3 align-middle" /> Not applicable / not yet configured
      </p>
    </>
  );
}

// ─── Main Component ──────────────────────────────────────────────────

export function ProgressReport() {
  return (
    <div className="max-w-6xl mx-auto px-4 py-12">
      <HeroBanner />
      <SectionJumpNav />
      <ExecSummary />
      <TimelineSection />
      <OutstandingSection />
      <ClientFeedbackSection />
      <SiteWideSection />
      <PerPropertySection />
      <PagesSection />
      <PropertyMatrix />
      <div className="mt-16 text-center text-sm text-gray-400">
        <p>This report is automatically generated and updated with each deployment.</p>
        <p className="mt-1">Questions? Contact your Repli implementation team.</p>
      </div>
    </div>
  );
}

export default ProgressReport;
